import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "components/sidebar";
import Content from "sections/main/content";
import { loadStripe } from "@stripe/stripe-js";
import { ToastContainer } from "react-toastify";
import { Elements } from "@stripe/react-stripe-js";
import { BrowserRouter as Router } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { useQuery } from "react-query";
import { getUsageData } from "api/usage";
import { getSubscriptionData } from "api/subscriptions";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";
import { useStripe } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import UpgradePromptModal from "components/UpgradePromptModal";
import { UPGRADE_PROMPT_SESSION_MILESTONES } from "enums/index";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Lives inside <Elements> so useStripe() is available.
// Owns ALL upgrade-flow state so nothing resets unexpectedly.
function UpgradeFlow() {
  const stripe = useStripe();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(null);
  const shownMilestonesRef = useRef(new Set());

  const { data: usageData } = useQuery("userUsage", getUsageData, {
    retry: false,
    staleTime: 10000,
  });

  const { data: stripeProducts } = useQuery(
    "stripe-products",
    getAllStripeProducts,
    { retry: false, staleTime: Infinity }
  );

  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery(
    "userSubscription",
    getSubscriptionData,
    { retry: false, staleTime: Infinity }
  );

  // Trigger promo modal at session milestones (free trial users only, on page load/refresh)
  useEffect(() => {
    if (!usageData?.sessions || !usageData?.isFreeTrial || subscriptionLoading) return;
    const isProUser =
      subscriptionData?.status === "active" ||
      subscriptionData?.status === "trialing";
    if (isProUser) return;
    const sessions = usageData.sessions;
    if (
      UPGRADE_PROMPT_SESSION_MILESTONES.includes(sessions) &&
      !shownMilestonesRef.current.has(sessions)
    ) {
      shownMilestonesRef.current.add(sessions);
      const timer = setTimeout(() => setShowUpgradeModal(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [usageData?.sessions, usageData?.isFreeTrial, subscriptionData?.status, subscriptionLoading]);

  // Also trigger when "Next Session" is clicked and a milestone is reached
  useEffect(() => {
    const handleMilestoneEvent = (e) => {
      const milestone = e.detail?.milestone;
      if (milestone && !shownMilestonesRef.current.has(milestone)) {
        shownMilestonesRef.current.add(milestone);
        setTimeout(() => setShowUpgradeModal(true), 2000);
      }
    };
    window.addEventListener("nirva-upgrade-milestone", handleMilestoneEvent);
    return () => window.removeEventListener("nirva-upgrade-milestone", handleMilestoneEvent);
  }, []);

  const handleCloseUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  const handleClickGetStarted = async (event, priceId) => {
    event.preventDefault();
    setLoading(priceId);
    try {
      const response = await createCheckoutSession({ priceId });
      const result = await stripe.redirectToCheckout({ sessionId: response.id });
      if (result.error) {
        toast.error(result.error.message || "Failed creating checkout session");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error processing payment!");
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      {showUpgradeModal && (
        <UpgradePromptModal
          onClose={handleCloseUpgradeModal}
          stripeProducts={stripeProducts}
          usageData={usageData}
          subscriptionData={subscriptionData}
          loading={loading}
          handleClickGetStarted={handleClickGetStarted}
        />
      )}
    </>
  );
}

function App() {
  // Clarity analytics
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'production') {
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        var t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        var y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "ucj05airdf");
    }
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <Router>
        <div className="h-screen max-h-screen flex">
          <Sidebar />
          <Content />
        </div>
      </Router>

      <UpgradeFlow />

      <ToastContainer autoClose={1000} />
    </Elements>
  );
}

export default App;
